import { Nullable, UserWithToken } from '@ygoargentina/sdk/api/api.types.js';
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { LoginCredentials } from '../components/custom/login-form.js';
import { authService } from '@ygoargentina/sdk/api/auth-service.js';

export interface AuthContextType {
  /** Current authenticated user or null */
  user: Nullable<UserWithToken>;
  /** Whether initial auth check is in progress */
  isLoading: boolean;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Login function */
  login: (
    credentials: LoginCredentials
  ) => Promise<{ success: boolean; error?: string }>;
  /** Logout function */
  logout: () => Promise<void>;
  /** Update user state manually (e.g., after profile update) */
  setUser: (user: Nullable<UserWithToken>) => void;
  /**
   * Vuelve a pedir el usuario de la sesión (`/auth/me`) sin recargar. Lo usan
   * las apps cuando el API avisa `session:changed` (lo sumaron al staff de una
   * tienda u organizador, le cambiaron el rol). Si falla, deja la sesión como
   * estaba: no es un logout. Resuelve `true` si el usuario cambió: la app
   * invalida entonces lo que dependía de él (listas de organizadores, etc.).
   */
  refreshUser: () => Promise<boolean>;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export interface AuthProviderConfig {
  /** Service name for login tracking (e.g., 'FOLDER', 'TOURNAMENT_APP') */
  service?: string;
  /** Version for login tracking */
  version?: string;
  /** Callback when logout completes */
  onLogout?: () => void;
  /** Callback when login completes successfully */
  onLogin?: (user: UserWithToken) => void;
  /** Callback when auth check completes */
  onAuthCheckComplete?: (user: Nullable<UserWithToken>) => void;
  /** Callback cuando `refreshUser` trae el usuario actualizado */
  onUserRefreshed?: (user: UserWithToken) => void;
}

export interface AuthProviderProps {
  children: ReactNode;
  config?: AuthProviderConfig;
}

export function AuthProvider({ children, config = {} }: AuthProviderProps) {
  const [user, setUserState] = useState<Nullable<UserWithToken>>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMounted, setHasMounted] = useState(false);

  const {
    service = 'WEB',
    version = 'unknown',
    onLogout,
    onLogin,
    onAuthCheckComplete,
    onUserRefreshed,
  } = config;

  // Mark as mounted (client-side only)
  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Check auth only after mounting (client-side)
  useEffect(() => {
    if (!hasMounted) return;

    const checkAuth = async () => {
      try {
        const currentUser = await authService.getCurrentUser();

        if (currentUser) {
          setUserState(currentUser as UserWithToken);
          onAuthCheckComplete?.(currentUser as UserWithToken);
        } else {
          setUserState(null);
          onAuthCheckComplete?.(null);
        }
      } catch (error) {
        // Check if it's a network error (API might be restarting)
        const isNetworkError =
          error instanceof Error &&
          (error.message.toLowerCase().includes('failed to fetch') ||
            error.message.toLowerCase().includes('network') ||
            error.name === 'TypeError');

        if (isNetworkError) {
          console.warn(
            'Auth check failed due to network error, keeping session'
          );
        } else {
          console.error('Auth check failed:', error);
          setUserState(null);
          onAuthCheckComplete?.(null);
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [hasMounted, onAuthCheckComplete]);

  // Cross-tab logout detection via localStorage changes
  useEffect(() => {
    if (!hasMounted) return;

    // Que otra pestaña borre el CSRF del localStorage NO alcanza para cerrar la
    // sesión acá: lo borran también caminos que no son un logout (un error de
    // permisos que se leyó como sesión vencida) y la cookie sigue valiendo.
    // Desloguear igual dejaba la pantalla montada con el usuario en null: el
    // sidebar de un jugador sin rol, sin avatar, «Email no validado», hasta
    // recargar. Se le pregunta al API; sólo un «no hay sesión» explícito cierra.
    let checking = false;
    const handleStorageChange = async (event: StorageEvent) => {
      if (event.key !== 'ygoarg_csrf_token' || event.newValue !== null) return;
      if (checking) return;
      checking = true;

      try {
        const session = await authService.verifySession();

        if (session.status === 'none') {
          console.info('[auth] otra pestaña cerró la sesión', {
            service,
            reason: session.reason,
          });
          setUserState(null);
          onLogout?.();
          return;
        }

        console.warn(
          '[auth] otra pestaña borró el CSRF y el API no confirmó el cierre: no se desloguea',
          {
            service,
            status: session.status,
            reason: session.status === 'unknown' ? session.reason : undefined,
          }
        );
      } finally {
        checking = false;
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [hasMounted, onLogout, service]);

  const login = useCallback(
    async (
      credentials: LoginCredentials
    ): Promise<{ success: boolean; error?: string }> => {
      try {
        const response = await authService.login({
          ...credentials,
          service,
          version,
        });

        if (!response.error && response.data?.user) {
          const loggedUser = response.data.user;
          setUserState(loggedUser);
          onLogin?.(loggedUser);
          return { success: true };
        }

        // Handle error messages
        const errorMessage = response.message || 'Error al iniciar sesión';
        if (errorMessage.includes('user_not_found')) {
          return { success: false, error: 'user_not_found' };
        }
        if (errorMessage.includes('invalid_credentials')) {
          return { success: false, error: 'Credenciales inválidas' };
        }

        return { success: false, error: errorMessage };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Error al iniciar sesión';
        return { success: false, error: errorMessage };
      }
    },
    [service, version, onLogin]
  );

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUserState(null);
      onLogout?.();
    }
  }, [onLogout]);

  const setUser = useCallback((newUser: Nullable<UserWithToken>) => {
    setUserState(newUser);
  }, []);

  // Un pedido a la vez, más UNO de cola: un aviso que llega con un
  // `/auth/me` en vuelo no puede reusarlo (ese pedido pudo salir antes del
  // cambio y traer la sesión vieja), pero tampoco se hace un pedido por aviso.
  // El callback va en un ref para que `refreshUser` sea estable aunque la app
  // pase uno nuevo en cada render.
  const refreshInFlight = useRef<Promise<boolean> | null>(null);
  const refreshAgain = useRef(false);
  // El último usuario conocido, para saber si el refresco cambió algo.
  const latestUser = useRef<Nullable<UserWithToken>>(null);
  const onUserRefreshedRef = useRef(onUserRefreshed);
  useEffect(() => {
    latestUser.current = user;
  }, [user]);
  useEffect(() => {
    onUserRefreshedRef.current = onUserRefreshed;
  }, [onUserRefreshed]);

  const refreshUser = useCallback((): Promise<boolean> => {
    if (refreshInFlight.current) {
      refreshAgain.current = true;
      return refreshInFlight.current;
    }

    const run = async () => {
      let changed = false;
      do {
        refreshAgain.current = false;
        try {
          const currentUser = (await authService.getCurrentUser()) as
            | Nullable<UserWithToken>
            | undefined;
          if (!currentUser) continue;

          const same =
            JSON.stringify(latestUser.current) === JSON.stringify(currentUser);
          if (!same) {
            changed = true;
            latestUser.current = currentUser;
          }

          // Si no cambió nada, no se toca el estado: un usuario nuevo con el
          // mismo contenido re-dispara todo lo que depende de `user`.
          if (!same) {
            setUserState(currentUser);
            onUserRefreshedRef.current?.(currentUser);
          }
        } catch (error) {
          console.warn('No se pudo refrescar la sesión:', error);
        }
      } while (refreshAgain.current);
      return changed;
    };

    refreshInFlight.current = run().finally(() => {
      refreshInFlight.current = null;
    });

    return refreshInFlight.current;
  }, []);

  const contextValue = useMemo(
    () => ({
      user,
      isLoading,
      isAuthenticated: !!user,
      login,
      logout,
      setUser,
      refreshUser,
    }),
    [user, isLoading, login, logout, setUser, refreshUser]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

/**
 * Hook to access auth context
 * @throws Error if used outside of AuthProvider
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook to access auth context (returns undefined if not in provider)
 * Useful for components that work both with and without auth
 */
export function useAuthOptional(): AuthContextType | undefined {
  return useContext(AuthContext);
}

export default AuthProvider;
